import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { MessageCircle, Users, Sparkles, Star, Zap } from 'lucide-react';
import { useUsage } from '../context/UsageContext';
import { useFreemium } from '../context/FreemiumContext';
import api from '../lib/api';

// Fecha oficial de cierre del acceso gratuito global
// Miércoles 12 de agosto de 2026 · 23:59 hora Chile (UTC-4)
const FREEMIUM_CUTOFF_ISO = '2026-08-12T23:59:00-04:00';
const WA_NUM = '56998759833';
const WA_INDIVIDUAL_MSG = 'Hola. Soy usuario(a) de Portafolio Digital Docente y necesito orientación sobre la plataforma.';
const WA_INDIVIDUAL_LINK = `https://wa.me/${WA_NUM}?text=${encodeURIComponent(WA_INDIVIDUAL_MSG)}`;
// NOTA: El link al grupo Premium NO se hardcodea. Se solicita al backend
// en `/api/premium/wa-group` sólo si el usuario es Premium activo o admin.

function daysSince(iso) {
    if (!iso) return 999;
    try {
        const created = new Date(iso).getTime();
        return Math.floor((Date.now() - created) / (1000 * 60 * 60 * 24));
    } catch { return 999; }
}

export default function DashboardCommsCards({ user }) {
    const { planTipo, planActivo } = useUsage();
    const { modo: freemiumModo, counters: freemiumCounters, limites, unlimitedAccess, isAdmin, status: freemiumStatus } = useFreemium();
    const [premiumWaUrl, setPremiumWaUrl] = useState(null);
    const overrideActive = !!freemiumStatus?.global_access_override_active;
    const grantActive = !!freemiumStatus?.user_access_grant_active;

    const cutoffMs = useMemo(() => new Date(FREEMIUM_CUTOFF_ISO).getTime(), []);
    const cutoffPassed = Date.now() > cutoffMs;

    // Plan efectivo: usar UsageContext como fuente de verdad; caer al user.plan_tipo si no hay.
    const plan = (planTipo || user?.plan_tipo || 'gratuito').toLowerCase();
    const isPremium = plan === 'premium' && planActivo;
    const isPlataforma = plan === 'plataforma' && planActivo;
    const isPaid = isPlataforma || isPremium;

    // Solicita el link del grupo Premium sólo cuando el usuario ya es Premium
    // (o admin/unlimited). El link NUNCA se expone al bundle público.
    useEffect(() => {
        if (!(isPremium || unlimitedAccess || isAdmin)) {
            setPremiumWaUrl(null);
            return;
        }
        let alive = true;
        api.get('/premium/wa-group')
            .then((r) => { if (alive) setPremiumWaUrl(r.data?.url || null); })
            .catch(() => { if (alive) setPremiumWaUrl(null); });
        return () => { alive = false; };
    }, [isPremium, unlimitedAccess, isAdmin]);

    const daysReg = daysSince(user?.created_at);
    const daysLeftSupport = Math.max(0, 10 - daysReg);
    // Regla soporte individual WhatsApp:
    //   - Gratuitos: 10 días desde registro
    //   - Plan Plataforma o Premium: siempre visible
    const showWhatsAppIndividual = isPaid || daysLeftSupport > 0;

    return (
        <div className="space-y-4">
            {/* Card de contadores Freemium (usuarios registrados ≥ 13-ago 2026) */}
            {freemiumModo === 'freemium_nuevo' && (
                <div
                    className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-white p-5 shadow-sm"
                    data-testid="dashboard-freemium-counters"
                >
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
                            <Zap className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <h3 className="font-heading text-base font-bold text-indigo-900">
                                    Modo Freemium · Explora la plataforma
                                </h3>
                                <Link to="/activar-plan">
                                    <Button size="sm" className="bg-amber-600 hover:bg-amber-700 gap-1 text-xs" data-testid="freemium-counters-activar-btn">
                                        <Star className="h-3 w-3 fill-current" />
                                        Activar Plan
                                    </Button>
                                </Link>
                            </div>
                            <p className="mt-1 text-xs text-indigo-800 leading-relaxed">
                                Explora la plataforma con acciones gratuitas. Al agotar cualquier contador, tu trabajo permanece guardado.
                            </p>
                            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                                {[
                                    { key: 'borradores', label: 'Borradores' },
                                    { key: 'analisis_ia', label: 'Análisis IA' },
                                    { key: 'diagnosticos', label: 'Diagnósticos' },
                                    { key: 'stats_vistas', label: 'Estadísticas' },
                                ].map((c) => {
                                    const info = freemiumCounters?.[c.key] || { restantes: (limites?.[c.key] ?? 5), limite: (limites?.[c.key] ?? 5), usados: 0 };
                                    const rest = info.restantes ?? 0;
                                    const lim = info.limite ?? 5;
                                    const critical = rest <= 1;
                                    return (
                                        <div
                                            key={c.key}
                                            className={`rounded-lg border p-2 text-center ${critical ? 'border-amber-300 bg-amber-50' : 'border-indigo-200 bg-white'}`}
                                            data-testid={`freemium-counter-${c.key}`}
                                        >
                                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{c.label}</div>
                                            <div className={`mt-0.5 font-mono text-lg font-extrabold ${critical ? 'text-amber-700' : 'text-indigo-900'}`}>
                                                {rest}
                                                <span className="text-[10px] font-normal text-slate-400"> / {lim}</span>
                                            </div>
                                            <div className="text-[9px] text-slate-500">restantes</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Banner legacy bloqueado post-cutoff */}
            {freemiumModo === 'legacy_bloqueado' && !isPaid && !unlimitedAccess && !isAdmin && !overrideActive && !grantActive && (
                <div
                    className="rounded-2xl border-2 border-red-300 bg-gradient-to-br from-red-50 to-amber-50 p-5 shadow-sm"
                    data-testid="dashboard-legacy-blocked-banner"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-500 text-white">
                                <Sparkles className="h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="font-heading text-lg font-bold text-red-900">El período gratuito de exploración finalizó</h3>
                                <p className="text-sm text-red-900/90 leading-relaxed mt-0.5">
                                    Todo tu trabajo permanece guardado y disponible en modo lectura. Activa el Plan Plataforma para
                                    crear nuevos borradores, ejecutar análisis IA y generar diagnósticos.
                                </p>
                            </div>
                        </div>
                        <Link to="/activar-plan" className="shrink-0">
                            <Button className="bg-red-600 hover:bg-red-700 gap-2 w-full sm:w-auto" data-testid="dashboard-legacy-activar-btn">
                                <Star className="h-4 w-4 fill-current" />
                                Activar Plan
                            </Button>
                        </Link>
                    </div>
                </div>
            )}

            {/* Banner acceso gratuito / cutoff — solo para legacy pre-cutoff (no freemium nuevos, no unlimited/admin, no durante override) */}
            {!isPaid && !unlimitedAccess && !isAdmin && !overrideActive && !grantActive && freemiumModo !== 'freemium_nuevo' && freemiumModo !== 'legacy_bloqueado' && (
                cutoffPassed ? (
                    <div
                        className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm"
                        data-testid="dashboard-cutoff-banner"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white">
                                    <Sparkles className="h-5 w-5" />
                                </div>
                                <div>
                                    <h3 className="font-heading text-lg font-bold text-amber-900">Tu período gratuito terminó</h3>
                                    <p className="text-sm text-amber-900 leading-relaxed mt-0.5">
                                        Todo tu trabajo continúa almacenado. Ahora puedes elegir uno de nuestros planes para
                                        continuar desarrollando tu Portafolio Docente.
                                    </p>
                                </div>
                            </div>
                            <Link to="/activar-plan" className="shrink-0">
                                <Button className="bg-amber-600 hover:bg-amber-700 gap-2 w-full sm:w-auto" data-testid="dashboard-activar-plan-btn">
                                    <Star className="h-4 w-4 fill-current" />
                                    Activar Plan
                                </Button>
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div
                        className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 sm:p-5"
                        data-testid="dashboard-freemium-banner"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <p className="font-heading text-sm sm:text-base font-bold text-emerald-900">
                                    Acceso gratuito hasta el miércoles 12 de agosto de 2026
                                </p>
                                <p className="text-xs text-emerald-800 mt-0.5">
                                    Tu trabajo permanecerá guardado. Después de esa fecha podrás activar el plan que
                                    mejor se adapte a tu forma de trabajar.
                                </p>
                            </div>
                            <Link to="/activar-plan" className="shrink-0">
                                <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-900 hover:bg-emerald-100 gap-2" data-testid="dashboard-ver-planes-btn">
                                    Ver Planes
                                </Button>
                            </Link>
                        </div>
                    </div>
                )
            )}

            {/* WhatsApp individual (10 días para gratuitos · siempre para planes activos) */}
            {showWhatsAppIndividual && (
                <div
                    className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm"
                    data-testid="wa-individual-card"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green-100 text-green-700">
                                <MessageCircle className="h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="font-heading text-sm sm:text-base font-bold text-slate-900">💬 ¿Necesitas ayuda?</h3>
                                <p className="text-xs sm:text-sm text-slate-600 leading-snug mt-0.5">
                                    {isPaid
                                        ? 'Escríbenos directamente por WhatsApp. Como usuario con plan activo, tu soporte está siempre disponible.'
                                        : `Puedes escribirnos directamente por WhatsApp durante tus primeros días de uso de la plataforma. Te quedan ${daysLeftSupport} ${daysLeftSupport === 1 ? 'día' : 'días'} de soporte inicial.`}
                                </p>
                            </div>
                        </div>
                        <a
                            href={WA_INDIVIDUAL_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0"
                            data-testid="wa-individual-btn"
                        >
                            <Button className="bg-green-600 hover:bg-green-700 gap-2 w-full sm:w-auto">
                                <MessageCircle className="h-4 w-4" />
                                Consultar por WhatsApp
                            </Button>
                        </a>
                    </div>
                </div>
            )}

            {/* Aviso soporte expirado (gratuito post 10 días) */}
            {!isPaid && !showWhatsAppIndividual && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="wa-individual-expired">
                    Tu período de soporte inicial gratuito ha finalizado.{' '}
                    <Link to="/activar-plan" className="underline font-medium text-slate-800">
                        Conoce nuestros planes
                    </Link>{' '}
                    para continuar con acompañamiento y beneficios adicionales.
                </div>
            )}

            {/* Comunidad WhatsApp — visible sólo para usuarios Premium históricos (o admin/unlimited) */}
            {(isPremium || unlimitedAccess || isAdmin) && (
                <div
                    className="rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm"
                    data-testid="wa-community-card-premium"
                >
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white">
                            <Users className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-heading text-base font-bold text-emerald-900">👥 Comunidad Oficial Portafolio Docente 2026</h3>
                            <p className="mt-1 text-sm text-emerald-900 leading-relaxed">
                                Como usuario <strong>Premium</strong>, ya puedes acceder a la comunidad privada de WhatsApp.
                                Allí recibirás tutoriales, avisos, material complementario, recordatorios y consejos.
                            </p>
                            {premiumWaUrl && (
                                <a href={premiumWaUrl} target="_blank" rel="noopener noreferrer" className="inline-block mt-3" data-testid="wa-group-btn">
                                    <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                                        🟢 Ingresar a la Comunidad
                                    </Button>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
